import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Select,
  MenuItem,
  FormGroup,
  FormHelperText,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import hu from 'date-fns/locale/hu';

export const FORM_FIELD_TYPES = {
  SHORT_TEXT: 'SHORT_TEXT',
  MULTILINE_TEXT: 'MULTILINE_TEXT',
  YES_NO: 'YES_NO',
  CHECKBOXES: 'CHECKBOXES',
  DROPDOWN: 'DROPDOWN',
  DATE: 'DATE',
  DURATION: 'DURATION',
  PHONE: 'PHONE',
  NUMBER: 'NUMBER',
  EMAIL: 'EMAIL',
  TIME: 'TIME',
};

// Rendszermező szekciók: milyen sorrendben jelennek meg, és mi a szekció neve
const SYSTEM_SECTIONS = [
  {
    label: 'Esemény adatai',
    keys: ['eventDate', 'eventTime', 'guestCount', 'eventType', 'durationHours'],
    // Ezek a keyPairs kerülnek egy sorba (mindkettőnek jelen kell lenni)
    pairs: [['eventDate', 'eventTime']],
  },
  {
    label: 'Kapcsolattartó',
    keys: ['contactName', 'contactEmail', 'contactPhone'],
    pairs: [['contactEmail', 'contactPhone']],
  },
  {
    label: 'Helyszín',
    keys: ['eventAddress', 'eventCity', 'eventZipCode'],
    pairs: [],
  },
];

// Az összes ismert systemKey
const ALL_SYSTEM_KEYS = new Set(SYSTEM_SECTIONS.flatMap(s => s.keys));

const getValidationResult = (values, fields) => {
  const newErrors = {};
  let isValid = true;
  fields.forEach(field => {
    if (field.isRequired) {
      const value = values[field.id];
      if (value === null || value === undefined || value === '') {
        newErrors[field.id] = 'Ez a mező kötelező.';
        isValid = false;
      } else if (Array.isArray(value) && value.length === 0) {
        newErrors[field.id] = 'Legalább egy opció kiválasztása kötelező.';
        isValid = false;
      }
    }
  });
  return { newErrors, isValid };
};

const getDefaultValue = (field) => {
  if (field.defaultValue) return field.defaultValue;
  switch (field.type) {
    case FORM_FIELD_TYPES.CHECKBOXES: return [];
    case FORM_FIELD_TYPES.YES_NO: return null;
    case FORM_FIELD_TYPES.DATE:
    case FORM_FIELD_TYPES.TIME: return null;
    default: return '';
  }
};

// Rendezi a mezőket szekciókba + egyéni mezők csoportba
function buildSections(fields) {
  const bySystemKey = {};
  const customFields = [];

  fields.forEach(field => {
    if (field.systemKey && ALL_SYSTEM_KEYS.has(field.systemKey)) {
      bySystemKey[field.systemKey] = field;
    } else {
      customFields.push(field);
    }
  });

  const sections = [];

  SYSTEM_SECTIONS.forEach(sectionDef => {
    // Csak azokat a mezőket vesszük, amelyek tényleg szerepelnek a formban
    const sectionFields = sectionDef.keys
      .map(k => bySystemKey[k])
      .filter(Boolean);

    if (sectionFields.length === 0) return;

    // Meghatározzuk, melyik mezők kerülnek párba
    const pairSet = new Set(); // fieldId-k amik párban vannak
    const pairRows = [];

    sectionDef.pairs.forEach(([keyA, keyB]) => {
      const fieldA = bySystemKey[keyA];
      const fieldB = bySystemKey[keyB];
      if (fieldA && fieldB) {
        // Sorrendbe rendezzük: amelyiknek kisebb az order értéke, az az első
        const [first, second] = fieldA.order <= fieldB.order
          ? [fieldA, fieldB]
          : [fieldB, fieldA];
        pairRows.push({ type: 'pair', fields: [first, second] });
        pairSet.add(fieldA.id);
        pairSet.add(fieldB.id);
      }
    });

    // Párokba nem kerülő mezők: teljes szélességben
    const soloFields = sectionFields.filter(f => !pairSet.has(f.id));

    // Összefűzzük a sorokat a megfelelő order szerint
    const rows = [];
    const addedIds = new Set();

    sectionFields.forEach(field => {
      if (addedIds.has(field.id)) return;

      // Pár első tagja?
      const pairRow = pairRows.find(r => r.fields[0].id === field.id || r.fields[1].id === field.id);
      if (pairRow && !addedIds.has(pairRow.fields[0].id) && !addedIds.has(pairRow.fields[1].id)) {
        rows.push(pairRow);
        addedIds.add(pairRow.fields[0].id);
        addedIds.add(pairRow.fields[1].id);
      } else if (!addedIds.has(field.id)) {
        rows.push({ type: 'single', fields: [field] });
        addedIds.add(field.id);
      }
    });

    sections.push({ label: sectionDef.label, rows });
  });

  // Egyéni mezők szekció
  if (customFields.length > 0) {
    const rows = customFields.map(f => ({ type: 'single', fields: [f] }));
    sections.push({ label: sections.length > 0 ? 'Egyéb kérdések' : null, rows });
  }

  return sections;
}

// ── Mező input komponens ──────────────────────────────────────────────────────

const FieldInput = ({ field, value, onChange, error }) => {
  const placeholder = field.placeholder || undefined;

  switch (field.type) {
    case FORM_FIELD_TYPES.SHORT_TEXT:
      return (
        <TextField
          fullWidth variant="outlined" size="small"
          placeholder={placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error} helperText={error}
          inputProps={{ maxLength: 255 }}
        />
      );

    case FORM_FIELD_TYPES.MULTILINE_TEXT:
      return (
        <TextField
          fullWidth variant="outlined"
          multiline rows={3}
          placeholder={placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error} helperText={error}
        />
      );

    case FORM_FIELD_TYPES.EMAIL:
      return (
        <TextField
          fullWidth variant="outlined" size="small"
          type="email"
          placeholder={placeholder || 'pelda@email.hu'}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error} helperText={error}
        />
      );

    case FORM_FIELD_TYPES.PHONE:
      return (
        <TextField
          fullWidth variant="outlined" size="small"
          type="tel"
          placeholder={placeholder || '+36 20 123 4567'}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error} helperText={error}
          inputProps={{ maxLength: 20 }}
        />
      );

    case FORM_FIELD_TYPES.NUMBER:
      return (
        <TextField
          fullWidth variant="outlined" size="small"
          type="number"
          placeholder={placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error} helperText={error}
          inputProps={{ min: 0 }}
        />
      );

    case FORM_FIELD_TYPES.DURATION:
      return (
        <TextField
          fullWidth variant="outlined" size="small"
          placeholder={placeholder || 'Pl. 4 óra'}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error} helperText={error}
        />
      );

    case FORM_FIELD_TYPES.DATE:
      return (
        <DatePicker
          value={value ? new Date(value) : null}
          onChange={(date) => onChange(field.id, date ? date.toISOString() : null)}
          disablePast
          slotProps={{
            textField: {
              fullWidth: true, variant: 'outlined', size: 'small',
              error: !!error, helperText: error,
            },
          }}
        />
      );

    case FORM_FIELD_TYPES.TIME:
      return (
        <TimePicker
          value={value ? (() => {
            const [h, m] = value.split(':');
            const d = new Date();
            d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
            return d;
          })() : null}
          onChange={(time) => {
            if (time) {
              const hh = String(time.getHours()).padStart(2, '0');
              const mm = String(time.getMinutes()).padStart(2, '0');
              onChange(field.id, `${hh}:${mm}`);
            } else {
              onChange(field.id, null);
            }
          }}
          ampm={false}
          slotProps={{
            textField: {
              fullWidth: true, variant: 'outlined', size: 'small',
              error: !!error, helperText: error,
            },
          }}
        />
      );

    case FORM_FIELD_TYPES.YES_NO:
      return (
        <>
          <RadioGroup
            row
            value={value === true ? 'yes' : value === false ? 'no' : ''}
            onChange={(e) => onChange(field.id, e.target.value === 'yes' ? true : false)}
          >
            <FormControlLabel value="yes" control={<Radio size="small" />} label="Igen" />
            <FormControlLabel value="no" control={<Radio size="small" />} label="Nem" />
          </RadioGroup>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </>
      );

    case FORM_FIELD_TYPES.CHECKBOXES:
      return (
        <>
          <FormGroup>
            {(field.options || []).map((option, i) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    size="small"
                    checked={(value || []).includes(option)}
                    onChange={(e) => {
                      const current = value || [];
                      onChange(field.id, e.target.checked
                        ? [...current, option]
                        : current.filter(o => o !== option));
                    }}
                  />
                }
                label={<Typography variant="body2">{option}</Typography>}
              />
            ))}
          </FormGroup>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </>
      );

    case FORM_FIELD_TYPES.DROPDOWN:
      return (
        <>
          <Select
            fullWidth size="small"
            value={value || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            displayEmpty
            error={!!error}
          >
            <MenuItem value="" disabled>
              <Typography variant="body2" color="text.secondary">
                {placeholder || 'Válasszon…'}
              </Typography>
            </MenuItem>
            {(field.options || []).map((option, i) => (
              <MenuItem key={i} value={option}>{option}</MenuItem>
            ))}
          </Select>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </>
      );

    default:
      return (
        <TextField
          fullWidth variant="outlined" size="small"
          placeholder={placeholder}
          value={value || ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          error={!!error} helperText={error}
        />
      );
  }
};

// ── Egy mező (label + input) ──────────────────────────────────────────────────

const FieldBlock = ({ field, value, onChange, error }) => (
  <Box>
    <FormLabel
      sx={{
        display: 'block',
        mb: 0.5,
        fontSize: '0.8rem',
        fontWeight: 600,
        color: 'text.secondary',
        letterSpacing: 0.2,
      }}
    >
      {field.label}
      {field.isRequired && (
        <Typography component="span" color="error.main" sx={{ ml: 0.3 }}>*</Typography>
      )}
    </FormLabel>
    <FieldInput field={field} value={value} onChange={onChange} error={error} />
  </Box>
);

// ── Fő komponens ──────────────────────────────────────────────────────────────

const DynamicFormRenderer = ({ form, onFormChange, initialValues, showTitle = true, showErrors = false }) => {
  const [fieldValues, setFieldValues] = useState({});
  const [errors, setErrors] = useState({});
  const formIdRef = useRef(null);
  const stableInitialValues = initialValues || {};

  useEffect(() => {
    if (!form || !form.fields) return;
    if (form.id !== formIdRef.current) {
      const newInitial = {};
      form.fields.forEach(field => {
        newInitial[field.id] = stableInitialValues[field.id] !== undefined
          ? stableInitialValues[field.id]
          : getDefaultValue(field);
      });
      setFieldValues(newInitial);
      formIdRef.current = form.id;
    }
  }, [form, stableInitialValues]);

  const handleChange = useCallback((fieldId, value) => {
    setFieldValues(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  useEffect(() => {
    if (onFormChange && form && form.fields) {
      const { newErrors, isValid } = getValidationResult(fieldValues, form.fields);
      setErrors(newErrors);
      onFormChange(fieldValues, isValid);
    }
  }, [fieldValues, onFormChange, form]);

  if (!form || !form.fields) {
    return <Typography color="text.secondary">Nincs form definíció.</Typography>;
  }

  const sections = buildSections(form.fields);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Box>
        {showTitle && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Playfair Display, serif', color: 'primary.dark' }}>
              {form.title}
            </Typography>
            {form.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {form.description}
              </Typography>
            )}
          </Box>
        )}

        {sections.map((section, sIdx) => (
          <Box key={sIdx}>
            {/* Szekció fejléc */}
            {section.label && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, mt: sIdx > 0 ? 3 : 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    color: 'primary.main',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {section.label}
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            )}

            {/* Sorok */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {section.rows.map((row, rIdx) => (
                <Box
                  key={rIdx}
                  sx={
                    row.type === 'pair'
                      ? { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'end' }
                      : {}
                  }
                >
                  {row.fields.map(field => (
                    <FieldBlock
                      key={field.id}
                      field={field}
                      value={fieldValues[field.id]}
                      onChange={handleChange}
                      error={showErrors ? errors[field.id] : undefined}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </LocalizationProvider>
  );
};

export default DynamicFormRenderer;
